/* System defined core library */
import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import * as $ from 'jquery';
import * as moment from 'moment';
import { Observable, Observer, Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { DynamicComponentService } from '../../../shared/services/dynamic.component.service';
import { ExamFeatureService } from '../../../shared/services/exam-feature.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { AppointmentService } from '../../shared/services/appointment/appointment.service';
import { MessagingApiService } from '../../shared/services/messaging-api.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { PaymentApiService } from '../../shared/services/payment-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';
import { TimerService } from '../../../shared/services/timer.service';

/* User defined models */
import { EligibilityDetail } from '../../../shared/models/scheduling/eligibility.model';
import { EmailResponse } from '../../../shared/models/common/shared.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { CardDetail } from '../../../shared/models/scheduling/carddetail.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { CreatePaymentSessionResponse } from '../../../shared/models/scheduling/paymentSession.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { PersonDetail } from '../../../shared/models/scheduling/personalDetail.model';
import { ReminderDetails } from '../../../shared/models/scheduling/reminderdetail.model';
import { TextReminder } from '../../../shared/models/scheduling/textReminder.model';
import { Session } from '../../../shared/models/scheduling/session.model';

/* User defined enums/constants */
import { ComponentType, LogLevel, NavigationAction } from '../../../shared/enums/scheduling.enum';
import { SchedulingErrors, TermUrlConstants, voucherPaymentCodes } from '../../../shared/constants/scheduling.constant';

/* Images */
const validateImg = require('/Assets/images/confirmation/validate.png');
const moneyImg = require('/Assets/images/confirmation/money.png');
const createCalendarImg = require('/Assets/images/confirmation/createcalendar.png');

@Component({
  selector: 'app-reconfirmation',
  templateUrl: './reconfirmation.component.html',
  providers: [DynamicComponentService]
})
export class ReconfirmationComponent implements OnInit {

  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    if (!this.confirmed) {
      this.metricApiService.logNavigation('Verification', NavigationAction.backwardsNavigation);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: any) {
    if (this.isBookingInProgress) {
      let holdId = '';
      let confNum = '';
      if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        let session = this.selectedAppointments[0].exam.sessions.find(sess => {
          return sess.selected;
        });
        holdId = session.hold.id;
        confNum = session.confirmationNumber;
      } else {
        holdId = this.selectedAppointments[0].hold.id;
        confNum = this.selectedAppointments[0].ConfirmationNumber;
      }

      this.metricApiService.log(LogLevel.Debug, `Candidate unloaded the Confirmation page while booking was in progress.\nHold: ${holdId}\nConfirmationNumber: ${confNum}`);
    }
  }

  /* User defined private/public variables */
  public validate = validateImg;
  public money = moneyImg;
  public createCalendar = createCalendarImg;
  public booking: Booking;
  public candidate: Candidate;
  public candidateData: Candidate;
  public confirmationNumber: string = '';
  public confirmed: boolean = false;
  public countries: Country[];
  public currencyCode: string;
  public eligibility: EligibilityDetail[] = [];
  public emailResponse: EmailResponse;
  public errorMessage: string = '';
  public isCompleteApplicable: boolean = true;
  public isRescheduleAllowed: boolean = true;
  public metricsDetail: MetricDetails[];
  public pathLock: boolean = false;
  public personDetail: PersonDetail;
  public postedConfirmationList: string[] = [];
  public postedConfirmationNumber: string = '';
  public postedErrorMessage: string;
  public reminderDetails: ReminderDetails;
  public selectedAppointments: AppointmentDetail[] = [];
  public showPostingInProgress: boolean = false;
  public showTextReminder: boolean;
  public terminationText: string = 'reconfirmationComponent.termUrl';
  public totalAmount: number = 0;
  public totalExamDurations: number = 0;
  public voucherPayment: Payment[];
  public batteryConfig: BatteryConfig;
  public verbiageMessage: string;
  public paymentOrchestratorSessionDetail: CreatePaymentSessionResponse;
  public bookingRequestData: any;
  public examDetailsForSettlement: any;
  public confirmedErrorMessage: any;
  public rescheduledConfirmationList: string[] = [];
  public skipConfirmPage: boolean = false;
  public smsTextReminder: TextReminder;
  public isBookingInProgress: boolean = false;

  /* Service subscriptions */
  booking$: Subscription;
  candidateData$: Subscription;
  countries$: Subscription;
  eligibility$: Subscription;
  metricsDetail$: Subscription;
  postedConfirmationList$: Subscription;
  selectedAppointments$: Subscription;
  translateLanguage$: Subscription;
  voucherPayment$: Subscription;
  batteryConfig$: Subscription;
  paymentOrchestratorSessionDetail$: Subscription;
  bookingRequestData$: Subscription;
  examDetailsForSettlement$: Subscription;
  smsTextReminder$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private apptService: AppointmentService,
    private messagingApiService: MessagingApiService,
    private paymentApiService: PaymentApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private titleService: Title,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private timerService: TimerService,
    private metricApiService: MetricApiService,
    private bootstrapParams: BootstrapParams
  ) {
    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.candidateData$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
        if (!!this.candidate) {
          this.personDetail = new PersonDetail(
            this.candidate.FirstName,
            this.candidate.LastName,
            this.candidate.AddressLine1,
            this.candidate.AddressLine2,
            this.candidate.AddressLine3,
            this.candidate.PrimaryPhoneNumber,
            this.candidate.EmailAddress);
        }
      });

    this.countries$ = selectedExamDetailService.countries.subscribe(
      countries => {
        this.countries = countries;
      });

    this.eligibility$ = selectedExamDetailService.eligibilityDetails.subscribe(
      eligibility => {
        this.eligibility = eligibility;
      });

    this.metricsDetail$ = selectedExamDetailService.metricsDetail.subscribe(
      metricsDetail => {
        this.metricsDetail = metricsDetail;
      });

    this.postedConfirmationList$ = selectedExamDetailService.postedConfirmationList.subscribe(
      postedConfirmationList => {
        this.postedConfirmationList = postedConfirmationList;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.voucherPayment$ = selectedExamDetailService.voucherPayment.subscribe(
      voucherPayment => {
        this.voucherPayment = voucherPayment || [];
      });

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });

    this.paymentOrchestratorSessionDetail$ = selectedExamDetailService.paymentOrchestratorSessionDetail.subscribe(
      paymentOrchestratorSessionDetail => {
        this.paymentOrchestratorSessionDetail = paymentOrchestratorSessionDetail;
      });

    this.bookingRequestData$ = selectedExamDetailService.bookingRequestData.subscribe(
      bookingRequestData => {
        this.bookingRequestData = bookingRequestData;
      });

    this.examDetailsForSettlement$ = selectedExamDetailService.examDetailsForSettlement.subscribe(
      examDetailsForSettlement => {
        this.examDetailsForSettlement = examDetailsForSettlement;
      });

    this.smsTextReminder$ = selectedExamDetailService.textReminder.subscribe(
      textReminder => {
        this.smsTextReminder = textReminder;
      });

    this.currencyCode = this.selectedExamDetailService.rescheduleFee['currency'];
    this.pathLock = this.selectedExamDetailService.setReschedCancelPathLock;
    this.reminderDetails = new ReminderDetails();
    this.showTextReminder = this.selectedExamDetailService.showTextReminder;
    this.totalAmount = this.selectedExamDetailService.rescheduleFee['totalAmount'];
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    const clientCode = this.selectedExamDetailService.clientCode ? this.selectedExamDetailService.clientCode.toLowerCase() : '';
    if (clientCode === 'cfa' || clientCode === 'cfad' || clientCode === 'cfas') {
      this.terminationText = 'CFA_Cancel_Term';
    }

    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.ReconfirmationComponent)) { return; }

    this.verbiageMessage = this.isPaymentRequired() ? 'reconfirmationComponent.reviewInfo' : 'reconfirmationComponent.clickNextToCompleteTransaction';

    this.confirmationNumber = this.selectedExamDetailService.confirmationNumber;
    this.confirmed = this.selectedExamDetailService.isPaymentConfirmed;
    this.skipConfirmPage = this.selectedExamDetailService.skipConfirmPage;
    this.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber;
    this.postedErrorMessage = this.selectedExamDetailService.postedErrorMessage;

    if (this.selectedAppointments != null && this.selectedAppointments != undefined) {
      this.totalExamDurations = ExamFeatureService.calculateTotalHoldDuration(this.selectedAppointments);
    }

    if (this.confirmed) {
      this.isBookingInProgress = true;
      // Perform final reschedule
      this.schedulingApiService.completeBookingUpdate(
        this.bookingRequestData,
        this.paymentOrchestratorSessionDetail,
        this.examDetailsForSettlement,
        this.metricsDetail
      ).subscribe({
        next: (resp) => {
          this.showPostingInProgress = false;
          window.localStorage.removeItem(this.bootstrapParams.sessionGuid);

          if (!!resp
            && resp.status
            && !!resp.response
            && !!resp.response.tests
          ) {
            resp.confirmationNumber = '';
            let postedConfirmationList: string[] = [];
            this.rescheduledConfirmationList = [];

            resp.response.tests.forEach((test: any) => {
              let sessionsUnselected: Session[] = [];
              if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
                sessionsUnselected = this.selectedAppointments[0].exam.sessions.filter(s => !s.selected);
              }
              postedConfirmationList = postedConfirmationList.concat(sessionsUnselected.map(s => s.confirmationNumber));

              const selectedAppointment: any = this.selectedAppointments
                .filter((appointment: any) => { return appointment.exam.id === test.exam.id; });

              resp.confirmationNumber = resp.confirmationNumber
                .concat(test.sessions[0].confirmationNumber + ' (' + selectedAppointment[0].exam.name + ') <br />');

              postedConfirmationList = postedConfirmationList.concat(test.sessions.map((s: Session) => {
                return s.confirmationNumber;
              }));
              this.rescheduledConfirmationList.push(test.sessions[0].confirmationNumber);
            });

            // set confirmation list
            this.selectedExamDetailService.setPostedConfirmationList(postedConfirmationList);
            // Remove </br> tag end of the string
            resp.confirmationNumber = resp.confirmationNumber
              .substring(0, resp.confirmationNumber.length - 6);

            // Update Document Title
            this.titleService.setTitle('ProScheduler - Appointment Rescheduled');

            this.selectedExamDetailService.postedConfirmationNumber = resp.confirmationNumber;
            this.selectedExamDetailService.postedErrorMessage = null;

            this.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber;

            this.isBookingInProgress = false;

            if (!!this.smsTextReminder && this.smsTextReminder.isNotificationSelected) {
              const emailResps: EmailResponse[] = [];
              if (this.rescheduledConfirmationList.length > 0) {
                this.rescheduledConfirmationList.forEach((confirmation) => {
                  this.messagingApiService.
                    text(this.smsTextReminder.countryISDCode.concat(this.smsTextReminder.mobileNumber), "SMSText", confirmation).subscribe(mailResponse => {
                      const emailResp: EmailResponse = new EmailResponse();
                      emailResp.successful = mailResponse.successful;
                      emailResp.emailType = 'SMSText';
                      emailResps.push(emailResp);
                      if (emailResps.length === this.rescheduledConfirmationList.length) {
                        this.emailResponse = emailResp;
                        this.messagingApiService.setEmailState(emailResp);

                        if (this.skipConfirmPage) {
                          this.goToTerminationUrl();
                        }
                      }
                    });
                });
              }
            } else {
              if (this.skipConfirmPage) {
                this.goToTerminationUrl();
              }
            }

            this.selectedExamDetailService.isFlowComplete = true;
          } else if (!!resp.errorId) {
            this.isBookingInProgress = false;
            this.confirmedErrorMessage = {
              id: resp.errorId,
              message: 'errors.error',
              description: resp.message
            };
            this.postedConfirmationNumber = 'Error';
          }
        },
        error: (err) => {
          this.isBookingInProgress = false;
          this.showPostingInProgress = false;
          window.localStorage.removeItem(this.bootstrapParams.sessionGuid);

          this.confirmedErrorMessage = {
            id: SchedulingErrors.Default,
            message: 'errors.error',
            description: 'errors.thereWasAnIssue'
          };
          this.postedConfirmationNumber = 'Error';
        }
      });
    }
  }

  /* API Post call to reschedule the appointment */
  submit(postData: any) {

    if (!this.isPaymentRequired()) {
      this.timerService.clearClock();
    }

    if (!this.confirmed) {
      this.validateVoucher().subscribe((canProceed: boolean) => {
        if (canProceed) {
          this.isRescheduleAllowed = true;

          if (!this.isPaymentRequired()) {
            // Set selected Exam id and hold id in test array and pass it to reschedule booking.
            const tests: any[] = [];
            const examDetailsForSettlement: any[] = [];

            this.selectedAppointments.forEach((selectedAppointment: AppointmentDetail) => {

              if (!!selectedAppointment.exam.sessions && selectedAppointment.exam.sessions.length) {
                tests.push({
                  "exam": {
                    "id": selectedAppointment.exam.id
                  },
                  "sessions": []
                });
                selectedAppointment.exam.sessions.forEach((session) => {
                  if (session.selected) {
                    tests[0]["sessions"].push({
                      "hold": {
                        "id": session.hold.id
                      },
                      'confirmationNumber': session.confirmationNumber
                    });
                  }
                })
              } else {
                tests.push({
                  'exam': {
                    'id': selectedAppointment.exam.id
                  },
                  'sessions': [{
                    'hold': {
                      'id': selectedAppointment.hold.id
                    },
                    'confirmationNumber': selectedAppointment.ConfirmationNumber
                  }]
                });
              }

              examDetailsForSettlement.push({
                'confirmationNumber': selectedAppointment.ConfirmationNumber,
                'fee': 0
              });
            });

            let eligId = undefined;
            if (!!this.eligibility && !!this.eligibility.length && !!this.eligibility[0] && !!this.eligibility[0].id) {
              eligId = this.eligibility.filter(e => e.exam.id === this.selectedAppointments[0].exam.id)[0].id;
            }

            const bookingRequestData = {
              'id': this.booking.id,
              'candidate': { 'id': this.booking.candidate.Id },
              'eligibility': !!eligId ? { 'id': eligId } : undefined,
              'tests': tests
            };

            bookingRequestData['payments'] = [];

            this.selectedExamDetailService.postedConfirmationNumber = '';

            this.postedErrorMessage = '';
            this.showPostingInProgress = true;

            // Final Reschedule Call
            this.schedulingApiService
              .reScheduleBookingWithoutPayment(bookingRequestData, examDetailsForSettlement, this.metricsDetail, this.selectedExamDetailService.isBackToBackRequired)
              .subscribe(reSchedulePostResp => {
                if (reSchedulePostResp.status) {
                  this.selectedExamDetailService.isPaymentConfirmed = true;
                  let postedConfirmationList: string[] = [];
                  reSchedulePostResp.response.tests.forEach((test: any) => {
                    let sessionsUnselected: Session[] = [];
                    if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
                      sessionsUnselected = this.selectedAppointments[0].exam.sessions.filter(s => !s.selected)
                    }
                    postedConfirmationList = postedConfirmationList.concat(sessionsUnselected.map(s => s.confirmationNumber));

                    const selectedAppointment: any = this.selectedAppointments
                      .filter((appointment: any) => { return appointment.exam.id === test.exam.id; });

                    let confnums = test.sessions.map((s: Session) => {
                      return (s.confirmationNumber + "<br />");
                    })
                    this.selectedExamDetailService.postedConfirmationNumber = this.confirmationNumber
                      .concat(confnums + " (" + selectedAppointment[0].exam.name + ") <br />");


                    postedConfirmationList = postedConfirmationList.concat(test.sessions.map((s: Session) => {
                      return s.confirmationNumber;
                    }));
                  });

                  this.selectedExamDetailService.setPostedConfirmationList(postedConfirmationList);

                  this.confirmed = true;
                  this.postedErrorMessage = null;
                  this.titleService.setTitle('ProScheduler - Appointment Rescheduled');

                  if (this.reminderDetails.notificationSelected) {
                    const emailResps: EmailResponse[] = [];
                    this.reminderDetails.reminderType = 'SMSText';

                    if (this.postedConfirmationList.length > 0) {
                      this.postedConfirmationList.forEach((confirmation) => {
                        this.messagingApiService
                          .text(this.reminderDetails.contactNumber, this.reminderDetails.reminderType, confirmation)
                          .subscribe({
                            next: (mailResponse) => {
                              const emailResp: EmailResponse = new EmailResponse();
                              emailResp.successful = mailResponse.successful;
                              emailResp.emailType = 'SMSText';
                              emailResps.push(emailResp);
                              if (emailResps.length === this.postedConfirmationList.length) {
                                this.emailResponse = emailResp;
                                this.messagingApiService.setEmailState(emailResp);
                              }
                            },
                            error: (err) => {
                              // TODO: Error Handling
                            }
                          });
                      });
                    }
                  }
                  this.showPostingInProgress = false;
                  this.routingService.rescheduleFwdNavigation(ComponentType.ReconfirmationComponent);
                  if (!this.selectedExamDetailService.skipConfirmPage) {
                    this.postedConfirmationNumber = this.selectedExamDetailService
                      .postedConfirmationNumber.substring(0, this.selectedExamDetailService.postedConfirmationNumber.length - 6);

                    this.selectedExamDetailService.postedConfirmationNumber = this.postedConfirmationNumber;
                  }
                  this.selectedExamDetailService.setprevSelectedHoldObj([]);
                } else {
                  this.showPostingInProgress = false;
                  this.selectedExamDetailService.isPaymentConfirmed = false;
                  this.selectedExamDetailService.postedConfirmationNumber = 'Error';
                  this.postedErrorMessage = reSchedulePostResp.message;
                }
              });
          } else {
            this.routingService.rescheduleFwdNavigation(ComponentType.ReconfirmationComponent);
          }
        } else {
          this.isRescheduleAllowed = false;
        }
      });
    }
  }

  getClass() {
    if (this.isPaymentRequired()) {
      return 'remaining-payment';
    } else {
      return 'text-highlight verification-blue';
    }
  }

  getApptDetail(appointmentDetail: AppointmentDetail, sessionIndex: number) {
    if (appointmentDetail.exam.sessions && appointmentDetail.exam.sessions.length < 1) return appointmentDetail;
    const session = appointmentDetail.exam.sessions[sessionIndex];
    let appointmentSlot =
      new AppointmentSlot(null, null, new Date(this.utilityService.getFormatToFullMMDDYYYY(session.slot.startDateTime, "/")), session.duration, session.site, session.slot.testTime, session.slot.testEndTime);
    appointmentDetail.appointmentSlot = appointmentSlot;
    return appointmentDetail;
  }

  /* Use to go back a page. */
  previous(event: any): void {
    this.routingService.backwardNavigation(ComponentType.ReconfirmationComponent);
  }

  /* returns isPaymentRequired from service to avert race condition issue from payment calculation from previous page navigation*/
  isPaymentRequired(): boolean {
    return this.selectedExamDetailService.isPaymentRequired;
  }

  /* isDisable */
  isDisable(): boolean {
    const checkErrorMessage: boolean = (this.postedErrorMessage !== '' && this.postedErrorMessage !== null) ? true : false;
    return (checkErrorMessage || !this.isCompleteApplicable || !this.isRescheduleAllowed);
  }

  /* Use to go back to home page. */
  goHome(event: any): void {
    this.selectedExamDetailService.setPostedConfirmationList(null);
    this.selectedExamDetailService.rescheduleFee = {
      totalAmount: 0,
      currency: null
    };
    this.routingService.homeNavigation();
  }

  /* Should link to client page be displayed */
  isTermUrl() {
    return !!this.selectedExamDetailService.terminationUrl;
  }

  /* Go to client page after appointment is scheduled. */
  goToTerminationUrl(): void {
    this.routingService.navigateToTerm(TermUrlConstants.Rescheduled);
  }

  /* Navigate away from proscheduler(to term/prometric site) */
  goToPrometric(): void {
    this.routingService.navigateAwayFromProscheduler();
  }

  /* Use to send confirmation & receipt email notification to user */
  email(emailto: string, emailtype: string): void {
    $('.email-list-icon').addClass('no-outline');
    const emailResps: EmailResponse[] = [];
    if (this.postedConfirmationList != null && this.postedConfirmationList != undefined && this.postedConfirmationList.length > 0) {
      this.postedConfirmationList.forEach((confirmation) => {
        this.messagingApiService.email(emailto, emailtype, confirmation).subscribe({
          next: (mailResponse) => {
            const emailResp: EmailResponse = new EmailResponse();
            emailResp.successful = mailResponse.successful;
            emailResp.emailType = (emailtype === 'eConfirmation') ? 'confirmation' : 'receipt';
            emailResps.push(emailResp);
            if (emailResps.length === this.postedConfirmationList.length) {
              this.emailResponse = emailResp;
              this.messagingApiService.setEmailState(emailResp);
            }
          },
          error: () => {
            // TODO: Error Handling
          }
        });
      });
    }
  }

  print() {
    $('.print-list-icon').addClass('no-outline');
  }

  onTextReminderChanged(textReminder: TextReminder): void {
    this.reminderDetails.notificationSelected = textReminder.isNotificationSelected;
    if (this.reminderDetails.notificationSelected) {
      if (textReminder.country != null && textReminder.country != undefined &&
        textReminder.mobileNumber !== '' && textReminder.mobileNumber != undefined && textReminder.mobileNumber != null && !isNaN(Number(textReminder.mobileNumber))) {
        this.reminderDetails.contactNumber = textReminder.countryISDCode.concat(textReminder.mobileNumber);
        this.isCompleteApplicable = true;
      } else {
        this.isCompleteApplicable = false;
      }
    } else {
      this.isCompleteApplicable = true;
    }
  }

  /* Returns the total duration of selected appointments */
  getTotalDuration() {
    this.totalExamDurations = ExamFeatureService.calculateTotalDuration(this.selectedAppointments);
  }

  /* Reset Payment confirmed when component is unloaded */
  ngOnDestroy() {
    if (this.confirmed) {
      this.selectedExamDetailService.setPostedConfirmationList(null);
    }
  }

  validateVoucher() {
    const voucherPayment = this.voucherPayment.filter(p => voucherPaymentCodes.indexOf(p.paymentType.toLowerCase()) !== -1);

    if (!voucherPayment.length) {
      return new Observable((observer: Observer<any>) => {
        observer.next(true);
        observer.complete();
      });
    }
    let voucherDetail: CardDetail = new CardDetail();
    voucherDetail.currency = voucherPayment[0].currency;
    const voucherRequestModel = {};
    const examDetails: any[] = [];
    this.selectedExamDetailService.voucherTransactionId = voucherPayment[0].transactionId;

    this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
      examDetails.push({
        'examId': appointment.exam.id,
        'amount': voucherPayment[0].amount
      });
    });

    // picking the site of first exam
    // TODO: handle this for multiple exams?
    const selectedSite = this.selectedAppointments[0].appointmentSlot.site;

    voucherRequestModel['card'] = voucherDetail;
    voucherRequestModel['sessions'] = examDetails;
    // just picking first exam for now
    voucherRequestModel['examDuration'] = this.selectedAppointments[0].appointmentSlot.duration;
    voucherRequestModel['applicationCode'] = 'pro';
    voucherRequestModel['siteId'] = selectedSite ? selectedSite.id : this.selectedExamDetailService.proctorSiteId;

    // Setting site country to demographic country for proctor
    let countryId = undefined;
    if (!!selectedSite) {
      countryId = selectedSite.location.address.country;
    } else {
      let demoCountry = '';
      // Checking both candidate details and defaultDetails for a demographic country
      if (!!this.candidate && !!this.candidate.details) {
        demoCountry = this.candidate.details['COUNTRY'];
      } else if (!!this.candidate && !!this.candidate.defaultDetails) {
        demoCountry = this.candidate.defaultDetails['country'];
      }

      // Country should be a required demographic for voucher payment
      if (!!demoCountry) {
        let country = this.countries.find(c => c.name.toLowerCase() === demoCountry.toLowerCase());
        countryId = !!country ? country.id : undefined;
      }

    }
    voucherRequestModel['siteCountry'] = countryId;
    voucherRequestModel['testDate'] = moment(this.selectedAppointments[0].appointmentSlot.startDateTime).format('YYYY-MM-DDTHH:mm:ss');
    voucherRequestModel['confirmationNumber'] = this.selectedAppointments[0].ConfirmationNumber;
    voucherRequestModel['candidateId'] = this.candidate.Id;

    const transactionId = voucherPayment[0].transactionId;

    return new Observable((observer: Observer<any>) => {
      this.paymentApiService.validateVoucherDetails(voucherRequestModel, transactionId).subscribe({
        next: (response) => {
          if (response.isValid) {
            observer.next(true);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  canShowReceiptBtn(): boolean {
    return this.apptService.canShowReceiptBtn(this.selectedAppointments);
  }

  //check if total duration can be displayed, based on the type of exam
  canShowDuration(): boolean {
    return this.apptService.canShowDuration(this.selectedAppointments);
  }

  isBookingComplete(): boolean {
    return this.confirmed && !!this.postedConfirmationNumber && !this.confirmedErrorMessage && !this.skipConfirmPage;
  }

  isBookingError(): boolean {
    return !!this.confirmedErrorMessage && !!this.confirmedErrorMessage.id;
  }

  showWaitView(): boolean {
    return this.confirmed && !this.isBookingError() && (!this.postedConfirmationNumber || this.skipConfirmPage)
  }
}
